import { isNullish } from '@/utils/helper'
import XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import moment from 'moment'

export const createExcelBlob = (excelData) => {
  if (!Array.isArray(excelData)) {
    return null
  }

  //for dateformat in excelData, change to DD-MM-YYYY
  excelData.forEach((row) => {
    Object.keys(row).forEach((key) => {
      const value = row[key]

      if (isNullish(row[key])) {
        row[key] = ''
      }

      if (key.includes('Date') || key.includes('date') || key.includes('createdAt') || key.includes('updatedAt')) {
        // console.log(key + row[key])
        if (typeof row[key] === 'string') {
          row[key] = row[key].split('T')[0]
        }
      }
    })
  })

  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.json_to_sheet(excelData)

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

  // Convert the workbook to a blob
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const excelBlob = new Blob([new Uint8Array(wbout)], {
    type: 'application/octet-stream',
  })

  return excelBlob
}

export const getFileExtension = (filename) => {
  if (isNullish(filename)) {
    return ''
  }
  const parts = filename.split('.')
  if (parts.length > 1) {
    return parts.pop().toLowerCase()
  } else {
    return ''
  }
}

export const createExcelFromAoA = (arrayOfArrays) => {
  if (!Array.isArray(arrayOfArrays) || !arrayOfArrays.length) {
    return null
  }

  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(arrayOfArrays)

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

  // Convert the workbook to a blob
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const excelBlob = new Blob([new Uint8Array(wbout)], {
    type: 'application/octet-stream',
  })

  return excelBlob
}

export const createExcelWithOptions = async (headerMapping, data, optionConfigs = [], additionalData = {}) => {
  if (!Array.isArray(data)) {
    return null
  }

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Sheet1')

  const sourceRow = data.find((row) => row && !Array.isArray(row) && typeof row === 'object') ?? null
  const resolvedHeaderKeys =
    Object.keys(headerMapping ?? {}).length > 0 ? Object.keys(headerMapping) : sourceRow ? Object.keys(sourceRow) : []

  if (!resolvedHeaderKeys.length) {
    return null
  }

  const resolvedHeaderMapping = headerMapping ?? {}

  const formattedData = data.map((row) => {
    if (Array.isArray(row)) {
      return row
    }

    const normalizedRow = {}

    resolvedHeaderKeys.forEach((key) => {
      const value = row?.[key]

      if (key.includes('Date') || key.includes('date') || key.includes('createdAt') || key.includes('updatedAt')) {
        if (value instanceof Date || moment(value).isValid()) {
          normalizedRow[key] = moment(value).format('DD-MMM-YYYY')
        } else {
          normalizedRow[key] = value
        }
        return
      }

      normalizedRow[key] = value
    })

    return normalizedRow
  })

  const headerRow = resolvedHeaderKeys.map((key) => resolvedHeaderMapping[key] ?? key)

  // Explicitly write header row so exports work for both array rows and object rows.
  worksheet.addRow(headerRow)

  formattedData.forEach((item) => {
    if (Array.isArray(item)) {
      worksheet.addRow(item)
      return
    }

    worksheet.addRow(resolvedHeaderKeys.map((key) => item[key] ?? ''))
  })

  // Define dropdown options
  for (const { options, columnKey } of optionConfigs) {
    // Apply dropdown to cells
    const safeOptions = options.map((opt) => opt.replace(/"/g, '""').replace(/,/g, ''))

    if (options.length > 0 && columnKey !== '') {
      const listSheet = workbook.addWorksheet('DropdownOptions_' + columnKey)
      listSheet.state = 'veryHidden'

      safeOptions.forEach((opt, i) => {
        listSheet.getCell(`A${i + 1}`).value = opt
      })

      const rowCount = formattedData.length > 50 ? formattedData.length : 50

      for (let i = 2; i <= rowCount + 1; i++) {
        worksheet.getCell(`${columnKey}${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`DropdownOptions_${columnKey}!$A$1:$A$22`],
        }
      }
    }
  }

  if (additionalData && Object.keys(additionalData).length > 0) {
    const listSheet = workbook.addWorksheet('Config')
    let dataCount = 0
    for (const [key, value] of Object.entries(additionalData)) {
      listSheet.getCell(`A${dataCount + 1}`).value = key
      listSheet.getCell(`B${dataCount + 1}`).value = value
      dataCount++
    }
  }

  // Generate Excel file and trigger download
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

  return blob
}

export const readExcelFile = (fileStream) => {
  if (isNullish(fileStream)) {
    return []
  }

  const normalizedStream = fileStream instanceof ArrayBuffer ? new Uint8Array(fileStream) : fileStream

  const workbook = XLSX.read(normalizedStream, {
    type: 'array',
    cellDates: true,
  })

  if (!workbook.SheetNames.length) {
    return []
  }

  const firstSheetName = workbook.SheetNames[0]
  const worksheet = workbook.Sheets[firstSheetName]

  if (!worksheet) {
    return []
  }

  return XLSX.utils.sheet_to_json(worksheet, {
    defval: '',
    raw: false,
  })
}

export const readSpecificCellsFromExcel = async (file, sheetNamePattern, cellAddresses) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Find matching sheet name (ignoring spaces)
        const targetSheetName = workbook.SheetNames.find(
          (name) =>
            name.includes(sheetNamePattern) || name.replace(/\s/g, '').includes(sheetNamePattern.replace(/\s/g, ''))
        )

        if (!targetSheetName || !workbook.Sheets[targetSheetName]) {
          return resolve({})
        }

        const worksheet = workbook.Sheets[targetSheetName]
        const results = {}
        for (const cellAddress of cellAddresses) {
          const cell = worksheet[cellAddress]
          results[cellAddress] = cell ? cell.v : null
        }
        resolve(results)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

export const getFormattedDataFromExcelFile = async (file, columnMapping) => {
  if (isNullish(file) || !Array.isArray(columnMapping) || columnMapping.length === 0) {
    return []
  }

  const data = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target.result)
    reader.onerror = reject
    reader.readAsBinaryString(file)
  })

  const workbook = XLSX.read(data, { type: 'binary' })
  const sheetName = workbook.SheetNames[0]

  if (!sheetName || !workbook.Sheets[sheetName]) {
    return []
  }

  const sheet = workbook.Sheets[sheetName]
  const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 })
  const header = sheetData[0] || []

  console.log('sheetData', sheetData)

  return sheetData
    .slice(1)
    .map((row) => {
      const obj = {}

      header.forEach((_, index) => {
        const rowValue = row[index]

        const headerKey = columnMapping[index].includes('Select')
          ? columnMapping[index].replace('Select', '')
          : columnMapping[index]

        if (isNullish(rowValue)) {
          obj[headerKey] = ''
          return
        }

        if (headerKey.includes('Date')) {
          if (typeof rowValue === 'number') {
            const excelEpoch = new Date(1899, 11, 30)
            const daysMs = rowValue * 24 * 60 * 60 * 1000
            obj[headerKey] = new Date(excelEpoch.getTime() + daysMs)
          } else {
            obj[headerKey] = rowValue
          }
          return
        }

        obj[headerKey] = typeof rowValue === 'number' ? parseFloat(rowValue) : rowValue
      })

      return obj
    })
    .filter((dataRow) => Object.values(dataRow).some((value) => !isNullish(value) && value !== ''))
}
